import { Box, MenuItem, Stack, TextField } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { Button, Typography } from "@platform/design-system";
import { forwardRef, useImperativeHandle, useRef } from "react";

import { AgeBandAmountsField } from "./AgeBandAmountsField";
import {
  CustomTextEditorFields,
  CustomTextModeToggle,
} from "./CustomTextEditorFields";
import { usePopupEditorKeyboard } from "./usePopupEditorKeyboard";
import {
  type GuaranteeIssueEditorProps,
  useGuaranteeIssueEditor,
} from "./useGuaranteeIssueEditor";

export type {
  GuaranteeIssueCellEditorParams,
  GuaranteeIssueScalarInput,
} from "./useGuaranteeIssueEditor";

// Fixed size so adding bands never resizes/repositions the popover (matches
// AgeReductionScheduleCellEditor). Content scrolls.
const POPOVER_WIDTH = 440;
const POPOVER_HEIGHT = 400;

/**
 * AG Grid popup editor for Voluntary Life Guarantee Issue: a type plus gated
 * salary-multiplier / dollar-cap / additional-amount inputs, or — for the
 * VL-only `ageBasedAmounts` type — a list of `$X for ages Y–Z` bands. State and
 * persistence live in useGuaranteeIssueEditor.
 */
export const GuaranteeIssueCellEditor = forwardRef<
  unknown,
  GuaranteeIssueEditorProps
>(function GuaranteeIssueCellEditor(props, ref) {
  const editor = useGuaranteeIssueEditor(props);

  useImperativeHandle(ref, () => ({
    getValue: editor.getValue,
    isPopup: () => true,
    isCancelBeforeStart: () => false,
    isCancelAfterEnd: () => false,
  }));

  const { editingCustomText, showBands } = editor;

  const containerRef = useRef<HTMLDivElement | null>(null);
  usePopupEditorKeyboard(containerRef, editor.handleSave, editor.handleCancel);

  return (
    <Box
      ref={containerRef}
      sx={{
        p: 2,
        width: POPOVER_WIDTH,
        height: POPOVER_HEIGHT,
        display: "flex",
        flexDirection: "column",
        backgroundColor: "background.paper",
        boxShadow: 3,
        borderRadius: 1,
      }}
    >
      <Stack
        spacing={1.5}
        sx={{ flex: "1 1 auto", minHeight: 0, overflowY: "auto" }}
      >
        <Typography variant="subtitle1">
          <strong>Edit Benefit Value</strong>
        </Typography>

        {editor.customTextSupported && (
          <CustomTextModeToggle
            editMode={editor.editMode}
            onChange={editor.setEditMode}
          />
        )}

        {!editingCustomText && (
          <TextField
            select
            size="small"
            label="Type"
            value={editor.draftType}
            onChange={(e) => editor.changeType(e.target.value)}
            fullWidth
          >
            {props.typeOptions.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </TextField>
        )}

        {!editingCustomText &&
          editor.visibleScalars.map((input) => (
            <TextField
              key={input.key}
              size="small"
              type="number"
              label={input.label}
              value={editor.draftScalars[input.key] ?? ""}
              onChange={(e) => editor.setScalar(input.key, e.target.value)}
              error={(editor.errors[input.key] ?? "") !== ""}
              helperText={editor.errors[input.key] || undefined}
              inputProps={{ min: 0 }}
              fullWidth
            />
          ))}

        {!editingCustomText && showBands && (
          <AgeBandAmountsField
            bands={editor.bands}
            showErrors={editor.showBandErrors}
            onUpdate={editor.updateBand}
            onRemove={editor.removeBand}
          />
        )}

        {editingCustomText && (
          <CustomTextEditorFields
            content={editor.draftCustomText}
            onUpdate={editor.setDraftCustomText}
            onCharacterCount={editor.setDraftCharacterCount}
            showHelperCaption={false}
          />
        )}
      </Stack>

      {!editingCustomText && showBands && (
        <Button
          size="sm"
          variant="tertiary"
          startIcon={<AddIcon />}
          onClick={editor.addBand}
          sx={{ alignSelf: "flex-start", flexShrink: 0, mt: 1 }}
        >
          Add band
        </Button>
      )}

      <Stack
        direction="row"
        spacing={1}
        justifyContent="flex-end"
        sx={{ pt: 1.5, flexShrink: 0 }}
      >
        <Button size="sm" variant="secondary" onClick={editor.handleCancel}>
          Cancel
        </Button>
        <Button size="sm" variant="primary" onClick={editor.handleSave}>
          Save
        </Button>
      </Stack>
    </Box>
  );
});
